/**
 * Performance optimization utilities for weak devices
 */

interface PerformanceConfig {
  maxConcurrentRequests: number;
  imageQuality: number;
  enableVirtualization: boolean;
  prefetchEnabled: boolean;
}

class PerformanceOptimizer {
  private static instance: PerformanceOptimizer;
  private config: PerformanceConfig;

  private constructor() {
    this.config = this.detectOptimalConfig();
  }

  static getInstance(): PerformanceOptimizer {
    if (!PerformanceOptimizer.instance) {
      PerformanceOptimizer.instance = new PerformanceOptimizer();
    }
    return PerformanceOptimizer.instance;
  }

  /**
   * Detect optimal configuration based on device capabilities
   */
  private detectOptimalConfig(): PerformanceConfig {
    const isLowEndDevice = this.isLowEndDevice();
    const hasSlowConnection = this.hasSlowConnection();

    return {
      maxConcurrentRequests: isLowEndDevice ? 2 : 6,
      imageQuality: isLowEndDevice || hasSlowConnection ? 60 : 80,
      enableVirtualization: true, // Always enable for better performance
      prefetchEnabled: !isLowEndDevice && !hasSlowConnection,
    };
  }

  /**
   * Check if device appears to be low-end
   */
  private isLowEndDevice(): boolean {
    // Check available memory
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      if (memInfo.jsHeapSizeLimit < 1073741824) { // < 1GB
        return true;
      }
    }

    // Check number of CPU cores
    if ('hardwareConcurrency' in navigator) {
      if (navigator.hardwareConcurrency <= 2) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if connection is slow
   */
  private hasSlowConnection(): boolean {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return connection.effectiveType === 'slow-2g' || 
             connection.effectiveType === '2g' ||
             connection.effectiveType === '3g';
    }
    return false;
  }

  /**
   * Get current performance configuration
   */
  getConfig(): PerformanceConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<PerformanceConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Optimize images based on device capabilities
   */
  getOptimizedImageUrl(originalUrl: string, width?: number): string {
    if (!originalUrl) return originalUrl;

    const config = this.getConfig();
    const quality = config.imageQuality;

    // For Supabase storage URLs, we can't use query parameters
    // but we can implement client-side optimization logic here
    if (originalUrl.includes('supabase')) {
      // Return original URL as Supabase doesn't support transform params
      return originalUrl;
    }

    // For other image services, add optimization parameters
    const separator = originalUrl.includes('?') ? '&' : '?';
    return `${originalUrl}${separator}quality=${quality}${width ? `&width=${width}` : ''}`;
  }

  /**
   * Debounce function for performance
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Throttle function for performance
   */
  throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

export { PerformanceOptimizer };